import type {
  StatusFlowScope,
  statusActionTypeValues,
} from './itinerary-status-machine';
import { z } from 'zod';

/**
 * XT-798 — the action control mechanism: ONE declaration of every side effect a
 * status change can have, what it needs to be told, and how it reads on screen.
 *
 * Phase 1 could only DESCRIBE what the code already did, and derived those
 * descriptions at read time so they could not drift. That made the screen
 * honest but inert. This catalog is what makes an action configurable: it is
 * the contract between three things that must never disagree —
 *
 *   - the EDITOR, which renders a form from `params` so a non-developer can add
 *     an action and be asked for exactly what it needs;
 *   - the EXECUTOR, which reads a stored `config` and performs it;
 *   - the LOG, which names what ran using the same labels the editor showed.
 *
 * A new action type is added HERE first. If it is not in the catalog the editor
 * cannot offer it, which is the point: the failure mode this replaces is a side
 * effect that exists only as a hardcoded branch nobody can see or change.
 *
 * ## Why parameters, and not more action types
 *
 * "Initialise items to S1 Non Requested" was one hardcoded behaviour. It is now
 * `init_item_operation_status` with an `operationStatusId` parameter, because
 * the status it sets is an ORGANIZATION's choice — S1 is TempoVIP's answer, not
 * a law. The alternative, one action type per target status, would put org data
 * in a shared enum and rot the moment an org renames a status.
 */

// --- Parameter declarations ----------------------------------------------

/**
 * How the editor should ask for a parameter. The `kind` picks the control; the
 * `source` kinds are populated from live org data at render time, so an action
 * can never name a status that no longer exists.
 */
export const actionParamKindValues = [
  // Free text, e.g. an email subject.
  'text',
  // A yes/no switch, e.g. "ask the consultant to confirm first".
  'boolean',
  // One of a fixed set declared in the catalog entry itself.
  'enum',
  // One of the ORG's operation statuses (S1 Non Requested, S2 Booked, …).
  'operation_status',
  // One of the ORG's itinerary statuses.
  'itinerary_status',
  // A field name on the itinerary, e.g. `cancellation_reason`.
  'itinerary_field',
  // One of the org's task collections.
  'task_collection',
] as const;
export const actionParamKindSchema = z.enum(actionParamKindValues);
export type ActionParamKind = z.infer<typeof actionParamKindSchema>;

export type ActionParam = {
  /** Key inside the action's stored `config`. */
  key: string;
  /** Label the editor shows. */
  label: string;
  kind: ActionParamKind;
  /** Required parameters block saving until answered. */
  required: boolean;
  /** Only for `kind: 'enum'`. */
  options?: { value: string; label: string }[];
  /** One line under the control, explaining the consequence of the choice. */
  hint?: string;
};

/** When an action runs, relative to the status change. */
export const actionPhaseValues = ['on_enter', 'on_exit'] as const;
export const actionPhaseSchema = z.enum(actionPhaseValues);
export type ActionPhase = z.infer<typeof actionPhaseSchema>;

/**
 * A dialog the consultant must satisfy before a status change that carries this
 * action is allowed to proceed.
 *
 * Derived from the action, never configured separately: attaching "record the
 * sale date" IS what makes the Sold confirmation appear. Two switches for one
 * behaviour would let someone configure a sale date with no confirmation, or a
 * confirmation that records nothing, and neither is a thing anyone wants.
 *
 * The prompt belongs to the ACTION rather than to a sales stage, which is what
 * makes it work everywhere: today `statusGateForStage` is duplicated in the
 * planner and the itineraries table and keys on stage 10 / 20, so a status the
 * org staged differently silently loses its dialog. Every surface that changes
 * a status resolves this instead.
 */
export const actionPromptValues = [
  // "Mark itinerary as SOLD?" — confirmation only, captures nothing.
  'confirm_sale',
  // Captures the cancellation reason and keeps it on the record.
  'collect_cancellation_reason',
] as const;
export const actionPromptSchema = z.enum(actionPromptValues);
export type ActionPrompt = z.infer<typeof actionPromptSchema>;

export type ActionCatalogEntry = {
  type: (typeof statusActionTypeValues)[number];
  /**
   * Which flow this action belongs to.
   *
   * One library serves both, and the type alone cannot decide: `stamp_sale_date`
   * acts on an ITINERARY and would have nothing to write on an operation status,
   * while `cascade_status_to_subitems` acts on a SERVICE. Scope is what stops
   * either being offered where it cannot run.
   */
  scope: StatusFlowScope;
  /** Short name, used in the editor's "add action" list and in the log. */
  label: string;
  /** What it does, in the language a non-developer reads. */
  summary: string;
  /** Phases this action may be attached to. */
  phases: readonly ActionPhase[];
  /** Whether it can be bound to a specific A → B step as well as to a status. */
  transitionBindable: boolean;
  params: readonly ActionParam[];
  /**
   * The dialog this action puts in front of the consultant, if any, worked out
   * from its own config. Returning null means the change proceeds silently.
   */
  prompt?: (config: Record<string, unknown>) => ActionPrompt | null;
};

// --- The catalog ----------------------------------------------------------

export const STATUS_ACTION_CATALOG: readonly ActionCatalogEntry[] = [
  {
    type: 'stamp_sale_date',
    scope: 'itinerary',
    label: 'Record a sale or cancellation date',
    summary:
      'Stamps the date the first time the itinerary reaches this status, and clears the opposing date. Never overwrites a date already recorded.',
    phases: ['on_enter'],
    transitionBindable: false,
    params: [
      {
        key: 'dateField',
        label: 'Date to record',
        kind: 'enum',
        required: true,
        options: [
          {
            value: 'sold_on',
            label: 'Sale date (clears the cancellation date)',
          },
          {
            value: 'cancelled_on',
            label: 'Cancellation date (clears the sale date)',
          },
        ],
        hint: 'Until now this was decided by the sales stage — 10–19 recorded a sale, 20 a cancellation.',
      },
    ],
    // Recording a sale IS the thing worth confirming, and capturing a
    // cancellation reason IS how a cancellation is recorded, so both dialogs
    // come with the action rather than being switched on beside it.
    prompt: (config) =>
      config.dateField === 'cancelled_on'
        ? 'collect_cancellation_reason'
        : 'confirm_sale',
  },
  {
    type: 'init_item_operation_status',
    scope: 'itinerary',
    label: 'Initialise item operation statuses',
    summary:
      'Sets an operation status on every item that has none yet, so the itinerary is workable in Operations. Never rewrites a status an item already has.',
    phases: ['on_enter'],
    transitionBindable: true,
    params: [
      {
        key: 'operationStatusId',
        label: 'Operation status to set',
        kind: 'operation_status',
        required: true,
        hint: 'Any of the organization’s operation statuses. This was hardcoded to S1 Non Requested, for a fixed list of organization ids.',
      },
    ],
  },
  {
    type: 'set_item_operation_status',
    scope: 'itinerary',
    label: 'Force every item to an operation status',
    summary:
      'Moves every item onto the chosen operation status, whatever it currently has. Sends no supplier messages.',
    phases: ['on_enter'],
    transitionBindable: true,
    params: [
      {
        key: 'operationStatusId',
        label: 'Operation status to set',
        kind: 'operation_status',
        required: true,
        hint: 'The cancellation sweep used S91 Cancelled, and matched the status by its NAME containing "cancel".',
      },
    ],
  },
  {
    type: 'assign_task_collection',
    scope: 'itinerary',
    label: 'Create tasks from a collection',
    summary:
      'Instantiates a task collection for the itinerary, creating its tasks.',
    phases: ['on_enter'],
    transitionBindable: true,
    params: [
      {
        key: 'taskCollectionId',
        label: 'Task collection',
        kind: 'task_collection',
        required: true,
      },
    ],
  },

  // --- Operation flow -----------------------------------------------------
  //
  // Every entry below is something the code ALREADY does when a SERVICE
  // changes operation status, so seeding an organization preserves its current
  // behaviour rather than inventing any.
  {
    type: 'cascade_status_to_subitems',
    scope: 'operation',
    label: 'Move sub-services along with the main service',
    summary:
      'When a main service reaches this status, every one of its active sub-services is moved to the same status, whatever they were on before.',
    phases: ['on_enter'],
    transitionBindable: true,
    params: [],
  },
  {
    type: 'clear_failed_messages',
    scope: 'operation',
    label: 'Clear failed supplier messages',
    summary:
      'Drops the failed WhatsApp sends recorded against the service, so the Failed Messages bullet stops counting them.',
    phases: ['on_enter'],
    transitionBindable: true,
    params: [],
  },
  {
    type: 'finalize_itinerary_when_complete',
    scope: 'operation',
    label: 'Finalize the itinerary once every service is complete',
    summary:
      'After the change, if every service on the itinerary sits on a completed status, the itinerary moves to the status chosen here.',
    phases: ['on_enter'],
    transitionBindable: false,
    params: [
      {
        key: 'itineraryStatusId',
        label: 'Itinerary status to move to',
        kind: 'itinerary_status',
        required: true,
        hint: 'This was resolved by ranking candidates named FINALIZADO, then by name, then by sales stage 10.',
      },
    ],
  },
  {
    type: 'roll_up_status_to_parent',
    scope: 'operation',
    label: 'Move the main service up once every sub-service is here',
    summary:
      'When a sub-service reaches this status, checks its main service: if every one of the other active sub-services is here too, the main service is moved here as well.',
    phases: ['on_enter'],
    transitionBindable: true,
    params: [],
  },
  {
    type: 'advance_when_end_time_passed',
    scope: 'operation',
    label: 'Move on once the service end time has passed',
    summary:
      'A service sitting on this status is moved to the status chosen here once its end time is in the past. Checked hourly; a service with no end time is never moved.',
    // Time-driven rather than change-driven: a status nobody touches still has
    // to advance, which is exactly what no `on_enter` / `on_exit` hook can do.
    // The phase is still `on_enter` because that is where the action is
    // ATTACHED — it describes the status a service is waiting in.
    phases: ['on_enter'],
    // Only ever attached to the status being waited in. A step describes a move
    // that already happened, and this action exists to make one happen later.
    transitionBindable: false,
    params: [
      {
        key: 'operationStatusId',
        label: 'Status to move to',
        kind: 'operation_status',
        required: true,
        hint: 'The Operation Flow moves a Delivered service to Done once the end time of the service passes.',
      },
    ],
  },
] as const;

/**
 * The actions one flow may use.
 *
 * The editor and the attach picker both filter through this rather than
 * showing the whole library: an action that cannot run where it is attached is
 * worse than one that is missing, because it fails silently at run time.
 */
export const actionCatalogForScope = (
  scope: StatusFlowScope
): readonly ActionCatalogEntry[] =>
  STATUS_ACTION_CATALOG.filter((e) => e.scope === scope);

/**
 * The catalog lists ONLY what the application actually performs today.
 *
 * `statusActionTypeValues` still carries `publish_preview`, `unpublish_preview`,
 * `send_email` and `create_notification` as wire vocabulary, but none of them
 * has an executor anywhere in the codebase, so none is offered here. Listing an
 * action a user can switch on that then does nothing is the `preview_trigger`
 * mistake — a declared behaviour read by no code — and this domain has made it
 * once already. They are added when something implements them.
 */

export const actionCatalogEntry = (
  type: string
): ActionCatalogEntry | undefined =>
  STATUS_ACTION_CATALOG.find((e) => e.type === type);

/**
 * Which required parameters an action is missing. The editor blocks saving on a
 * non-empty result, and the executor SKIPS such an action rather than guessing
 * — a half-configured action must do nothing, visibly, rather than something
 * unintended.
 */
export const missingActionParams = (
  type: string,
  config: Record<string, unknown>
): ActionParam[] => {
  const entry = actionCatalogEntry(type);
  if (!entry) return [];
  return entry.params.filter((p) => {
    if (!p.required) return false;
    const value = config[p.key];
    return value == null || value === '';
  });
};

/**
 * The dialog a move INTO `toStatusId` must satisfy, worked out from that
 * status's configured actions.
 *
 * This is what makes the prompt universal. `statusGateForStage` — the thing it
 * replaces — is duplicated in the planner and the itineraries table and keys on
 * the sales STAGE, so an org that staged its Sold status as anything but 10
 * silently lost its confirmation, and any new surface had to remember to call
 * it. Reading the actions instead means the dialog follows the behaviour it
 * belongs to, on every surface, including ones not written yet.
 *
 * At most one prompt: two dialogs for one status change is a decision nobody
 * asked for. Catalog order decides, and today only `stamp_sale_date` prompts.
 */
export const resolveActionPrompt = (
  actions: readonly {
    type: string;
    config: Record<string, unknown>;
    isEnabled?: boolean;
  }[]
): ActionPrompt | null => {
  for (const entry of STATUS_ACTION_CATALOG) {
    for (const action of actions) {
      if (action.type !== entry.type) continue;
      if (action.isEnabled === false) continue;
      const prompt = entry.prompt?.(action.config) ?? null;
      if (prompt) return prompt;
    }
  }
  return null;
};
